import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { CheckIcon, TriangleAlertIcon } from "lucide-react";

import "./autosave.scss";
import Message from "../../i18n/Message";
import Spinner from "../../assets/icons/Spinner";

/**
 * Debounced autosave. `saveFn` is an async function that persists the current state;
 * it should surface its own errors (e.g. via Notify) and re-throw so the indicator
 * shows a failed state. Call `schedule()` on every edit — the indicator shows "Saving…"
 * immediately and "Saved." once the trailing debounced save resolves.
 *
 * `saveFn` is read through a ref so the debounced call always runs the latest closure
 * (avoids persisting stale state captured at schedule time). Call `cancel()` to drop a
 * pending save (e.g. when the input became invalid). On unmount any pending save is
 * flushed so an edit made just before navigating away is not lost.
 */
export const useAutosave = (saveFn, delay = 600) => {
  const [status, setStatus] = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const saveRef = useRef(saveFn);
  saveRef.current = saveFn;
  const mountedRef = useRef(true);

  const debounced = useMemo(
    () =>
      debounce(() => {
        Promise.resolve()
          .then(() => saveRef.current())
          .then(() => {
            if (mountedRef.current) setStatus("saved");
          })
          .catch(() => {
            if (mountedRef.current) setStatus("error");
          });
      }, delay),
    [delay]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Persist a pending edit instead of dropping it when the component unmounts.
      debounced.flush();
    };
  }, [debounced]);

  const schedule = useCallback(() => {
    setStatus("saving");
    debounced();
  }, [debounced]);

  const cancel = useCallback(() => {
    debounced.cancel();
  }, [debounced]);

  return { status, schedule, cancel };
};

export const AutosaveIndicator = ({ status }) => {
  if (status === "saving") {
    return (
      <span className="autosave_status saving">
        <Spinner size={14} />
        <Message id="saving" />
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span className="autosave_status saved">
        <CheckIcon size={14} />
        <Message id="saved" />
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="autosave_status error">
        <TriangleAlertIcon size={14} />
        <Message id="autosave_failed" />
      </span>
    );
  }
  // Reserve vertical space so the row doesn't jump when the indicator appears.
  return <span className="autosave_status" aria-hidden="true" />;
};
